import { msg, type Message } from '../i18n/t.ts';
import { MAX_DISPLAY_NAME_CHARS, MIN_PASSWORD_CHARS, type FieldName } from './profile.ts';

/**
 * What an auth payload's `reason` says, as copy in the reader's language
 * (native-language UI spec §6.2). The server's `message` is English and is
 * never shown. Where the reason names a field, the copy goes under it, as the
 * client's own validation does.
 *
 * Only refusals are here. `SIGNUP_ACCEPTED` and `RESET_REQUESTED` come with
 * `ok: true`, and the screens answer them with their own "check your email"
 * state.
 */
export type Refusal = { field: FieldName | null; message: Message };

const REFUSALS: Record<string, Refusal> = {
  INVALID_EMAIL: { field: 'email', message: msg('field-email-invalid') },
  WEAK_PASSWORD: { field: 'password', message: msg('field-password-short', { min: MIN_PASSWORD_CHARS }) },
  NAME_REQUIRED: { field: 'displayName', message: msg('field-name-empty') },
  NAME_TOO_LONG: { field: 'displayName', message: msg('field-name-long', { max: MAX_DISPLAY_NAME_CHARS }) },
  NATIVE_LANGUAGE_IS_ENGLISH: { field: 'nativeLanguage', message: msg('field-language-english') },
  BAD_LEVEL: { field: 'cefrLevel', message: msg('auth-bad-level') },
  CREDENTIALS_REFUSED: { field: null, message: msg('signup-credentials-refused') },
  PASSWORD_REFUSED: { field: null, message: msg('auth-password-refused') },
  LINK_REFUSED: { field: null, message: msg('auth-link-refused') },
};

/**
 * The copy for a refusal. A reason this build does not know — Relay's
 * `%future added value`, from a newer server — still says something, and
 * never the server's English.
 */
export function refusalFor(reason: string | null | undefined): Refusal {
  if (reason && Object.hasOwn(REFUSALS, reason)) return REFUSALS[reason]!;
  return { field: null, message: msg('refusal-auth-failed') };
}
